import { useCallback, useEffect, useRef } from "react";
import { Divider } from "@mui/material";
import { useSelector } from "store/useSelector";
import {
  useSelectedDay,
  useDashboardPageContext,
} from "utils/customHooks/genericHooks";
import { useTransition } from "@react-spring/core";
import { animated } from "@react-spring/web";
import { RootState } from "store/store";
import { useDrag } from "react-use-gesture";
import { FullDayButton } from "./FullDayButton";
import { ShiftButtons } from "./ShiftButtons";
import { getIsCypressRunning } from "constants/constants";

export function AnimatedShiftButtonsLeftSide() {
  // const shifts = useSelector((state: RootState) => state.legacy.shifts);
  const todaysShifts = useSelector(
    (state: RootState) => state.legacy.todaysShifts
  );
  // const timeZoneMsOffset = useTimeZoneMsOffset();
  const selectedDay = useSelectedDay();
  const { isFullDaySelected } = useDashboardPageContext();
  const prevSelectedDayRef = useRef(selectedDay);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    prevSelectedDayRef.current = selectedDay;
  }, [selectedDay]);

  const scrollContainerToRight = useCallback(() => {
    setTimeout(() => {
      const shiftsContainers = document.querySelectorAll<HTMLDivElement>(
        `[class*=DateAndShiftPickerStyles] .leftSide`
      );
      shiftsContainers.forEach(d => {
        d.scrollLeft = d.scrollWidth;
      });
    }, 0);
  }, []);

  useEffect(() => {
    if (isFullDaySelected) {
      scrollContainerToRight();
    }
  }, [isFullDaySelected, scrollContainerToRight]);

  // when we switch days, animate the shift buttons left or right
  const isForwards = selectedDay.range.lt > prevSelectedDayRef.current.range.lt;
  const transitionLeftRightAsDayChanges = useTransition(selectedDay, {
    key: d => d,
    ...getTransitionLeftRightAnimationProps(isForwards, 70),
    immediate: getIsCypressRunning(),
    onRest: () => {
      // Call the scroll function when the animation completes
      if (isFullDaySelected) {
        scrollContainerToRight();
      }
    },
  });

  // horizontal scroll when dragging
  const bind = useDrag(
    ({
      event,
      active,
      down,
      dragging,
      delta: [x, y],
      _dragTarget,
      ...rest
    }) => {
      if (down || dragging) {
        event.stopPropagation();
      }
      const shiftsContainer = document.querySelector(
        `.leftSide.day-ge-${selectedDay.range.ge}`
      );
      if (shiftsContainer) {
        shiftsContainer.scrollLeft += -x;
      }
    }
  );

  return transitionLeftRightAsDayChanges((style, day) => {
    return (
      <animated.div
        ref={containerRef}
        className={`leftSide day-ge-${day.range.ge} ${
          todaysShifts.length > 5 ? " shrink" : ""
        }`}
        style={style}
        {...bind()}
      >
        <ShiftButtons shifts={todaysShifts} />
        <Divider orientation="vertical" />
        <FullDayButton />
      </animated.div>
    );
  });
}
const getTransitionLeftRightAnimationProps = (isForwards, movementPx) => ({
  from: {
    transform: `translate(${isForwards ? movementPx : -movementPx}px,0px)`,
    opacity: 0,
  },
  enter: {
    transform: `translate(0px,0px)`,
    opacity: 1,
  },
  leave: {
    position: "absolute",
    transform: `translate(${
      isForwards ? -movementPx * 2 : movementPx * 2
    }px,0px)`,
    opacity: 0,
  },
});
